'use client';

import { FC, useEffect, useRef, useState } from 'react';
import FormattedPrice from '@/components/common/formatted-price/formatted-price';
import QuantityInput from '@/components/common/quantity-input/quantity-input';
import { Button } from '@/components/ui/button/button';
import { Image } from '@/components/ui/image/image';
import { useAllProductsByLocaleContext } from '@/hooks/use-all-products-by-locale';
import useCoreConfig from '@/hooks/use-core-config';
import useMyCart from '@/hooks/use-my-cart/use-my-cart';
import { useTranslation } from '@/providers/app-provider/app-provider';
import { useKeycloak } from '@/providers/keycloak-provider';
import { MyCartActionButtonsEnum } from '@/types/action-buttons/action-buttons';
import { SimpleProductInfo } from '@/types/products/simple-product-info';
import { GUMLET_PRESETS } from '@/utils/gumlet';
import { mapProductToSimpleProductInfo } from '@/utils/map-product-to-simple-product-info';
import { selectMainImage } from '@/utils/select-main-image';

interface Props {
  productIds: string[];
  ingredientsColor?: string;
}

const RecipeShoppingCart: FC<Props> = ({ productIds, ingredientsColor }) => {
  const { t } = useTranslation();
  const { addProductToMyCart, isPendingUpdateMyCartProductId } = useMyCart();
  const { keycloak } = useKeycloak();
  const { features } = useCoreConfig();
  const [isBuyAllPending, setIsBuyAllPending] = useState(false);
  const hasSeenCartUpdateRef = useRef(false);

  const isAuthenticated = keycloak?.authenticated;
  const shouldAllowCart = (!isAuthenticated && features.cartForGuests) || isAuthenticated;

  const { items: contextProducts } = useAllProductsByLocaleContext();

  const [quantities, setQuantities] = useState<Record<string, number>>(() =>
    Object.fromEntries(productIds.map(id => [id, 1])),
  );

  const cartItems = productIds
    .map(id => {
      const product = contextProducts.find(p => p.id === id);
      return product ? { id, product: mapProductToSimpleProductInfo(product) } : null;
    })
    .filter((item): item is { id: string; product: SimpleProductInfo } => item != null);

  const isAddingIngredients =
    isBuyAllPending ||
    (isPendingUpdateMyCartProductId !== undefined &&
      productIds.includes(isPendingUpdateMyCartProductId));

  useEffect(() => {
    if (!isBuyAllPending || !isAuthenticated) return;

    if (isPendingUpdateMyCartProductId !== undefined) {
      hasSeenCartUpdateRef.current = true;
    }

    if (hasSeenCartUpdateRef.current && isPendingUpdateMyCartProductId === undefined) {
      setIsBuyAllPending(false);
      hasSeenCartUpdateRef.current = false;
    }
  }, [isBuyAllPending, isAuthenticated, isPendingUpdateMyCartProductId]);

  if (cartItems.length === 0) return null;

  const handleBuyAll = () => {
    if (!shouldAllowCart || isAddingIngredients) return;

    hasSeenCartUpdateRef.current = false;
    setIsBuyAllPending(true);

    cartItems.forEach(({ id, product }) => {
      addProductToMyCart(
        { ...product, quantity: quantities[id] ?? 1 },
        { name: 'AddToCart', id, sourceUrl: window.location.href },
        MyCartActionButtonsEnum.PRODUCT_CARD,
      );
    });

    if (!isAuthenticated) {
      setIsBuyAllPending(false);
    }
  };

  return (
    <div className="flex flex-col overflow-hidden rounded-3xl border border-gray-200">
      <div className="flex flex-col gap-4 p-4">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold @md:text-base">{t?.common.buyIngredients}</span>
          <span className="bg-primary flex h-5 w-5 items-center justify-center rounded-full text-xs font-bold text-white">
            {cartItems.length}
          </span>
        </div>

        <div className="flex flex-col divide-y divide-gray-100">
          {cartItems.map(({ id, product }) => {
            const qty = quantities[id] ?? 1;
            const image = selectMainImage(product.images ?? [], GUMLET_PRESETS.thumbnail);
            return (
              <div key={id} className="flex items-center gap-3 py-3">
                <div className="h-20 w-20 shrink-0 overflow-hidden rounded-xl">
                  <Image
                    src={image}
                    alt={product.name}
                    width={80}
                    height={80}
                    loading="lazy"
                    classNames={{ img: 'h-20 w-20 object-contain' }}
                  />
                </div>
                <div className="flex flex-1 flex-col">
                  <span
                    className="text-sm font-semibold"
                    style={ingredientsColor ? { color: ingredientsColor } : undefined}
                  >
                    {product.name}
                  </span>
                  <FormattedPrice price={product.discountedPrice ?? product.price} size="sm" />
                </div>
                <QuantityInput
                  size="sm"
                  quantity={qty}
                  onChange={value => setQuantities(prev => ({ ...prev, [id]: value }))}
                />
              </div>
            );
          })}
        </div>
      </div>

      <Button
        color="primary"
        className="h-12 w-full rounded-none rounded-b-3xl font-semibold"
        isDisabled={!shouldAllowCart || isAddingIngredients}
        isLoading={isAddingIngredients}
        onPress={handleBuyAll}
      >
        {t?.common.buyIngredientsButton}
      </Button>
    </div>
  );
};

export default RecipeShoppingCart;
